import React from "react";
import {
  Box,
  Text,
  Select,
  Input,
  Spinner,
  Button,
  VStack,
  InputGroup
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { forwardRef } from "react";
import { ZoneRow } from "./ZoneRow";
import { QuantityInput } from "./QuantityInput";

export function AppointmentForm(props) {
  const {
    isAuthed,
    isAdminBooking,
    message,
    handleBooking,
    treatments,
    selectedTreatment,
    setSelectedTreatment,
    treatmentOptions,
    selectedOptionId,
    setSelectedOptionId,
    customInputValue,
    setCustomInputValue,
    optionError,
    totalPrice,
    isTattoo,
    isLaser,
    zoneQuantities,
    setZoneQuantities,
    closures,
    fullyBookedDates,
    date,
    setDate,
    timeSlots,
    selectedTime,
    setSelectedTime,
    loadingSlots,
    formatDateLocal,
    isBooking,
  } = props;

  const now = new Date();
  let availableSlots = [];
  let isPastDate = false;

  if (date) {
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (selectedDate < today) {
      isPastDate = true;
      availableSlots = [];
    } else if (selectedDate.getTime() === today.getTime()) {
      availableSlots = timeSlots.filter(slot => {
        const [h, m] = slot.split(':').map(Number);
        return h > now.getHours() || (h === now.getHours() && m > now.getMinutes());
      });
    } else {
      availableSlots = timeSlots;
    }
  }


  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <InputGroup mb={4} width="100%">
      <Input
        data-cy="dateInput"
        onClick={onClick}
        ref={ref}
        value={value}
        placeholder="Selecteer een datum"
        readOnly
        cursor="pointer"
        width="98.1%"
        height="40px"
        fontSize="md"
        marginLeft="0px"
      />
    </InputGroup>
  ));

  return (
    <Box
      maxW="500px"
      w="100%"
      p={5}
      boxShadow="lg"
      borderRadius="md"
      bg="white"
      mb="200px"
    >
      <Text fontSize="2xl" fontWeight="bold" mb={4}>
        Afspraak plannen
      </Text>

      {!isAuthed ? (
        <Text color="red.500" fontSize="lg" fontWeight="bold">
          {message}
        </Text>
      ) : (
        <>
          <Select
            data-cy="treatmentSelect"
            placeholder="Selecteer een behandeling"
            value={selectedTreatment}
            onChange={(e) => setSelectedTreatment(e.target.value)}
            mb={4}
          >
            {treatments.map((treatment) => {
              let extraLabel = "";
              if (treatment.name === "Verwijdering tatoeage") {
                extraLabel = "- Voer lengte in";
              } else if (
                treatment.options &&
                treatment.options.length > 0
              ) {
                extraLabel = "- Kies een optie";
              } else {
                extraLabel = `- €${treatment.price}`;
              }
              return (
                <option
                  key={treatment.treatmentId}
                  value={treatment.treatmentId}
                >
                  {treatment.name} {extraLabel}
                </option>
              );
            })}
          </Select>

          {treatmentOptions.length > 0 && (() => {
            if (isTattoo) {
              return (
                <>
                  <Text fontWeight="medium" mb={2}>
                    Deze behandeling kost €5 per centimeter
                  </Text>
                  <Input
                    type="number"
                    placeholder="Lengte in cm (hele getallen)"
                    value={customInputValue ?? ""}
                    onChange={(e) => setCustomInputValue(e.target.value)}
                    mb={4}
                    ml="0px"
                  />
                  {optionError && <Text color="red.500">{optionError}</Text>}
                  {totalPrice > 0 && (
                    <Text fontWeight="bold" color="blue.500" mb={2}>
                      Totaalprijs: €{totalPrice}
                    </Text>
                  )}
                </>
              );
            } else if (isLaser) {
              return (
                <>
                  <Text fontWeight="medium" mb={6}>
                    Selecteer per zone hoeveel beurten (max 7, alle beurten na 5
                    zijn gratis)
                  </Text>
                  <VStack align="start" spacing={3} mb={4} width="100%">
                    {treatmentOptions.map((opt) => {
                      const currentQuantity = zoneQuantities[opt.id] ?? 0;
                      return (
                        <ZoneRow
                          key={opt.id}
                          option={opt}
                          quantity={currentQuantity}
                          onChange={(id, value) =>
                            setZoneQuantities((prev) => ({
                              ...prev,
                              [id]: value,
                            }))
                          }
                        />
                      );
                    })}
                  </VStack>
                  {totalPrice > 0 && (
                    <Text fontWeight="bold" color="blue.500" mb={2}>
                      Totaalprijs: €{totalPrice}
                    </Text>
                  )}
                </>
              );
            } else {
              return (
                <>
                  <Select
                    placeholder="Selecteer een optie"
                    value={selectedOptionId}
                    onChange={(e) => setSelectedOptionId(e.target.value)}
                    mt={6}
                    mb={4}
                  >
                    {treatmentOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name} - €{opt.price}
                      </option>
                    ))}
                  </Select>
                  {totalPrice > 0 && (
                    <Text fontWeight="bold" color="blue.500" mb={2}>
                      Totaalprijs: €{totalPrice}
                    </Text>
                  )}
                </>
              );
            }
          })()}

          <DatePicker
            selected={date}
            onChange={(selectedDate) => {
              setDate(selectedDate);
            }}
            dateFormat="yyyy-MM-dd"
            placeholderText="Selecteer een datum"
            filterDate={(day) => day.getDay() !== 5}
            excludeDates={[...closures, ...fullyBookedDates]}
            minDate={new Date()}
            customInput={<CustomDateInput />}
          />

          {loadingSlots ? (
            <Spinner />
          ) : (
            <Select
              data-cy="timeSelect"
              placeholder="Selecteer een tijdstip"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              mb={4}
            >
              {isPastDate ? (
                <option disabled>
                  Gelieve een datum in de toekomst te selecteren
                </option>
              ) : availableSlots.length > 0 ? (
                availableSlots.map((slot, index) => (
                  <option key={index} value={slot}>
                    {slot}
                  </option>
                ))
              ) : (
                <option disabled>Geen beschikbare tijdslots</option>
              )}
            </Select>
          )}


          <Button
            data-cy="confirmBooking"
            colorScheme={isAdminBooking ? "green" : "blue"}
            onClick={handleBooking}
            isLoading={isBooking}
            isDisabled={isBooking}
            width="100%"
            ml="0px"
          >
            Afspraak bevestigen
          </Button>

          {message && (
            <Text
              data-cy="bookingMessage"
              mt={4}
              color={message.includes("succesvol") ? "green.500" : "red.500"}>
              {message}
            </Text>
          )}
        </>
      )}
    </Box>
  );
}
